import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../infrastructure/api/client";
import {
  ApiError,
  type AnnouncementResponseDto,
  type CreateAnnouncementDto,
  type UpdateAnnouncementDto,
  type AddReactionDto,
} from "@tabipla/shared";

// Query keys
const announcementKeys = {
  all: ["announcements"] as const,
  list: (groupId: string) => [...announcementKeys.all, groupId] as const,
};

// Fetch announcements for a group
export function useAnnouncements(groupId: string | undefined) {
  return useQuery({
    queryKey: announcementKeys.list(groupId ?? ""),
    queryFn: async () => {
      const response = await apiClient.get<AnnouncementResponseDto[]>(
        `/api/groups/${groupId}/announcements`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch announcements");
      }
      return response.data;
    },
    enabled: !!groupId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Create announcement mutation
export function useCreateAnnouncement(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAnnouncementDto) => {
      const response = await apiClient.post<AnnouncementResponseDto>(
        `/api/groups/${groupId}/announcements`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create announcement");
      }
      return response.data;
    },
    onSuccess: (newAnnouncement) => {
      if (!newAnnouncement) return;
      queryClient.setQueryData<AnnouncementResponseDto[]>(announcementKeys.list(groupId), (old) => {
        if (!old) return [newAnnouncement];
        // Add to beginning (new announcements first, unless pinned items exist)
        const pinned = old.filter((a) => a.isPinned);
        const unpinned = old.filter((a) => !a.isPinned);
        return [...pinned, newAnnouncement, ...unpinned];
      });
    },
  });
}

// Update announcement mutation
export function useUpdateAnnouncement(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      announcementId,
      data,
    }: {
      announcementId: string;
      data: UpdateAnnouncementDto;
    }) => {
      const response = await apiClient.put<AnnouncementResponseDto>(
        `/api/groups/${groupId}/announcements/${announcementId}`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update announcement");
      }
      return response.data;
    },
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData<AnnouncementResponseDto[]>(announcementKeys.list(groupId), (old) => {
        if (!old) return [updated];
        const newList = old.map((a) => (a.id === updated.id ? updated : a));
        // Re-sort: pinned first, then by date
        return newList.sort((a, b) => {
          if (a.isPinned && !b.isPinned) return -1;
          if (!a.isPinned && b.isPinned) return 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
      });
    },
  });
}

// Delete announcement mutation
export function useDeleteAnnouncement(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (announcementId: string) => {
      const response = await apiClient.delete<null>(
        `/api/groups/${groupId}/announcements/${announcementId}`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete announcement");
      }
    },
    onSuccess: (_, announcementId) => {
      queryClient.setQueryData<AnnouncementResponseDto[]>(announcementKeys.list(groupId), (old) => {
        if (!old) return [];
        return old.filter((a) => a.id !== announcementId);
      });
    },
  });
}

// Toggle reaction mutation
export function useToggleReaction(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      announcementId,
      data,
    }: {
      announcementId: string;
      data: AddReactionDto;
    }) => {
      const response = await apiClient.post<AnnouncementResponseDto>(
        `/api/groups/${groupId}/announcements/${announcementId}/reactions`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to toggle reaction");
      }
      return response.data;
    },
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.setQueryData<AnnouncementResponseDto[]>(announcementKeys.list(groupId), (old) => {
        if (!old) return [updated];
        return old.map((a) => (a.id === updated.id ? updated : a));
      });
    },
  });
}
